import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import {
  Button,
  Checkbox,
  Container,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import {
  AcceptCompanyUser,
  acceptCompanyUserSchema
} from '@shared/dto/companies.dto'
import { CompanyUsersRelation } from '@shared/types/companies.types'
import { Permission } from '@shared/types/roles.types'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { LoaderButton } from '../../components/LoaderButton'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useAcceptCompanyUserMutation } from '../../hooks/useMutations/useCompaniesMutations'
import {
  useCompany,
  useCompanyRoles,
  useCompanyUser
} from '../../hooks/useQueries/useCompanies'

import SubHeaderProfile from '../../components/SubHeaderProfile'
import { useWorkspaces } from '../../hooks/useQueries/useWorkspaces'
import { useSnackbar } from '../../hooks/useSnackbar'
import { isDefaultRole } from '../../utils/user.utils'

const paperStyles = {
  padding: '1.5rem',
  borderRadius: '.25rem',
  background: '#232629'
}

export default function CompaniesAcceptInvitation() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const companyId = params.companyId!
  const userId = params.userId!
  const { userId: loggedUserId } = useAuthStore()
  const { data: user, isLoading: isCompanyUserLoading } = useCompanyUser(
    companyId,
    loggedUserId!
  )
  const emailFromStorage = sessionStorage.getItem('invitationEmail') || ''
  const { data: roles, isLoading: isRolesLoading } = useCompanyRoles(companyId)
  const { data: workspaces, isLoading: isWorkspacesLoading } =
    useWorkspaces(companyId)
  const { showSnackbar } = useSnackbar()
  const { data: company, isLoading: isCompanyLoading } = useCompany(companyId)

  const acceptCompanyUserMutation = useAcceptCompanyUserMutation(
    companyId,
    userId
  )

  const canAcceptMembers = useMemo(() => {
    return (
      user?.permissions?.includes(Permission.USERS_ACCEPT) ||
      user?.role?.fullAccess
    )
  }, [user])

  useEffect(() => {
    if (user && !canAcceptMembers) {
      navigate('/access-denied')
    }
  }, [canAcceptMembers, navigate, user])

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('invitationEmail')
    }
  }, [])

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty }
  } = useForm<AcceptCompanyUser>({
    defaultValues: {
      email: emailFromStorage,
      relation: '' as CompanyUsersRelation,
      jobTitle: '',
      roleId: '',
      workspaces: []
    },
    resolver: standardSchemaResolver(acceptCompanyUserSchema)
  })

  const onSubmit = (data: AcceptCompanyUser) => {
    acceptCompanyUserMutation.mutate(data, {
      onSuccess: () => {
        showSnackbar({
          message: t('accept_invitation_view.success'),
          severity: 'success'
        })
        navigate(`/companies/${companyId}/members`)
      },
      onError: () => {
        showSnackbar({
          message: t('accept_invitation_view.error'),
          severity: 'error'
        })
      }
    })
  }

  if (
    isCompanyUserLoading ||
    isRolesLoading ||
    isWorkspacesLoading ||
    isCompanyLoading
  ) {
    return (
      <Stack direction='column' spacing='1rem' alignItems='center'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Skeleton variant='text' width='6rem' height={40} />
          <Skeleton variant='text' width='6rem' height={40} />
        </Stack>

        <Stack direction='column' spacing='2rem'>
          <Stack direction='column' spacing='.5rem'>
            <Skeleton variant='text' width='20rem' height={50} />
            <Skeleton variant='text' width='30rem' height={40} />
          </Stack>

          <Stack direction='column' spacing='1.5rem'>
            <Skeleton variant='rectangular' width='36rem' height={56} />
            <Skeleton variant='rectangular' width='36rem' height={56} />
            <Skeleton variant='rectangular' width='36rem' height={56} />
          </Stack>

          <Skeleton variant='rectangular' width='8rem' height={40} />
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <SubHeaderProfile
        title={company?.name || ''}
        subtitle={t('create_invitation_view.title')}
        isDirty={isDirty}
      />

      <Container maxWidth={false} sx={{ maxWidth: '61.5rem' }}>
        <Stack direction='column' spacing='2rem' sx={{ marginY: '3rem' }}>
          <Typography
            variant='h5'
            sx={{ fontWeight: 'bold', padding: '1rem 1.25rem' }}
          >
            {t('create_invitation_view.title')}
          </Typography>

          <Stack direction='column' spacing='2rem'>
            <Paper elevation={1} sx={paperStyles}>
              <Stack direction='column' spacing='1.5rem'>
                <Stack direction='column' spacing='.5rem'>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                      {t('create_invitation_view.user_data.title')}
                    </Typography>
                    <Typography variant='body2'>
                      {t('create_company.required')}
                    </Typography>
                  </Stack>
                  <Typography variant='body1'>
                    {t('create_invitation_view.user_data.subtitle')}
                  </Typography>
                </Stack>

                <Stack direction='column' spacing='1.5rem'>
                  <TextField
                    {...register('email')}
                    required
                    variant='filled'
                    sx={{ background: 'text.secondary' }}
                    label={t('create_invitation_view.user_data.email')}
                    error={!!errors.email}
                    helperText={
                      errors.email && t(errors.email.message as string)
                    }
                    fullWidth
                  />

                  <Controller
                    name='relation'
                    control={control}
                    render={({ field }) => (
                      <FormControl variant='filled' fullWidth>
                        <InputLabel>
                          {t('create_invitation_view.user_data.relation')}
                        </InputLabel>
                        <Select
                          {...field}
                          displayEmpty
                          required
                          sx={{
                            background: 'text.secondary',
                            borderRadius: '0.25rem 0.25rem 0 0'
                          }}
                        >
                          {Object.values(CompanyUsersRelation).map(relation => (
                            <MenuItem key={relation} value={relation}>
                              {t(`relation.${relation}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <TextField
                    {...register('jobTitle')}
                    label={t('create_invitation_view.user_data.job_title')}
                    error={!!errors.jobTitle}
                    helperText={
                      errors.jobTitle && t(errors.jobTitle.message as string)
                    }
                    fullWidth
                    variant='filled'
                    sx={{ background: 'text.secondary' }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={1} sx={paperStyles}>
              <Stack direction='column' spacing='1.5rem'>
                <Stack direction='column' spacing='.5rem'>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                      {t('create_invitation_view.projects_and_role.title')}
                    </Typography>
                    <Typography variant='body2'>
                      {t('create_company.required')}
                    </Typography>
                  </Stack>
                  <Typography variant='body1'>
                    {t('create_invitation_view.projects_and_role.subtitle')}
                  </Typography>
                </Stack>

                <Stack direction='column' spacing='1.5rem'>
                  <Controller
                    name='roleId'
                    control={control}
                    render={({ field }) => (
                      <FormControl variant='filled' fullWidth>
                        <InputLabel>
                          {t('create_invitation_view.projects_and_role.role')}
                        </InputLabel>
                        <Select
                          {...field}
                          displayEmpty
                          required
                          fullWidth
                          renderValue={selected => {
                            const selectedRole = roles?.find(
                              role => role.id === selected
                            )

                            if (!selectedRole) return selected

                            return (
                              <Typography sx={{ textTransform: 'capitalize' }}>
                                {isDefaultRole(selectedRole.name)
                                  ? t(`role.${selectedRole.name}`)
                                  : selectedRole.name}
                              </Typography>
                            )
                          }}
                        >
                          {(roles || []).map(role => (
                            <MenuItem
                              key={role.id}
                              value={role.id}
                              sx={{
                                textTransform: 'capitalize'
                              }}
                            >
                              {isDefaultRole(role.name)
                                ? t(`role.${role.name}`)
                                : role.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name='workspaces'
                    control={control}
                    render={({ field }) => {
                      const selectedIds = field.value || []
                      const allWorkspaceIds =
                        workspaces?.map(workspace => workspace.id) || []
                      const allSelected =
                        selectedIds.length === allWorkspaceIds.length

                      const handleChange = (event: any) => {
                        const value = event.target.value

                        if (value[value.length - 1] === '__SELECT_ALL__') {
                          const newValue = allSelected ? [] : allWorkspaceIds
                          field.onChange(newValue)
                        } else {
                          field.onChange(value)
                        }
                      }

                      return (
                        <FormControl variant='filled' fullWidth>
                          <InputLabel>
                            {t(
                              'create_invitation_view.projects_and_role.choose_projects'
                            )}
                          </InputLabel>
                          <Select
                            {...field}
                            multiple
                            displayEmpty
                            onChange={handleChange}
                            value={field.value}
                            fullWidth
                            renderValue={selected => {
                              const selectedNames =
                                workspaces
                                  ?.filter(workspace =>
                                    selected.includes(workspace.id)
                                  )
                                  .map(workspace => workspace.name) || []

                              return selectedNames.join(', ')
                            }}
                          >
                            <MenuItem value='__SELECT_ALL__'>
                              <Checkbox checked={allSelected} />
                              <ListItemText
                                primary={t(
                                  'create_invitation_view.projects_and_role.all_projects'
                                )}
                              />
                            </MenuItem>

                            {(workspaces || []).map(workspace => (
                              <MenuItem key={workspace.id} value={workspace.id}>
                                <Checkbox
                                  checked={selectedIds.includes(workspace.id)}
                                />
                                <ListItemText primary={workspace.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ paddingTop: '1rem' }}
            >
              <Button
                variant='text'
                onClick={() => navigate(-1)}
                sx={{ color: 'text.primary', textTransform: 'none' }}
              >
                {t('common.cancel')}
              </Button>

              <LoaderButton
                variant='contained'
                disabled={
                  acceptCompanyUserMutation.isPending || !isValid || !isDirty
                }
                loading={acceptCompanyUserMutation.isPending}
                onClick={handleSubmit(onSubmit)}
                label={
                  acceptCompanyUserMutation.isPending
                    ? t('accept_invitation_view.sending')
                    : t('accept_invitation_view.button')
                }
                sx={{ textTransform: 'none', width: '14.5rem' }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </>
  )
}
